/**
 * What the friction and rubber boards record, carried to the platform.
 *
 *   friction.survey        a survey imported on /friction → one whole survey (reading:write)
 *   friction.test          a manual touchdown / midpoint / rollout test → a one-lap survey run
 *   friction.return        the operator's return, once filed → a friction test record (test:write)
 *   rubber.removal         a removal recorded on /rubber → finished work on the day it was done
 *   rubber.removal.amend   the removal record completed later → that record amended
 *   rubber.model           "save as this airport's figures" → the site's model figures (insight:run)
 *
 * The mappings are pure and exported, so each is tested on its own. Nothing
 * here imports the demo domain: this sits in the entry chunk.
 */
import { DataSourceError } from "../../../api/dataSource";
import { frictionKeys, RUBBER_MODEL } from "../../../api/frictionQueries";
import type { CompletedWorkInput, ModelParamsInput, NewFrictionSurvey } from "../../../domain/friction";
import { runwayReference, touchdownZone } from "../../../domain/friction";
import type { NewTestInput } from "../../../domain/testing";
import { t } from "../../../i18n";
import { AERODROME_OFFSET_HOURS } from "../../../lib/format";
import { registerWrite } from "../writes";

/* ── shapes the boards hand over ───────────────────────────────────────── */

/** A survey as the friction board holds it (SURVEY0, or what parseAsft built). */
export interface BoardSurvey {
  survey: {
    id: string;
    date: string;
    time?: string;
    runway?: string;
    device?: string;
    standard?: string;
    tyre?: string;
    tyre_pressure_bar?: number;
    water_film?: boolean;
    speed_kmh?: number;
    declared_length_m?: number;
    location?: string;
    levels?: { design?: number; planning?: number; minimum?: number };
    note?: string;
    demo?: boolean;
  };
  /** metres from the threshold each run started at, the end of each segment */
  d: number[];
  /** "RW17" → laps × readings */
  runs: Record<string, (number | null)[][]>;
}

/** A manual friction test on /rubber HISTORY. */
export interface BoardFrictionTest {
  id: string;
  end: string;
  date: string;
  device?: string;
  speed?: number;
  tdz: number;
  mid: number;
  roll: number;
  note?: string;
}

/** A removal on /rubber. */
export interface BoardRemoval {
  id: string;
  end: string;
  date: string;
  method?: string;
  area?: number | null;
  contractor?: string;
  hours?: number | null;
  muBefore?: number | null;
  muAfter?: number | null;
  provisional?: boolean;
  note?: string;
}

/** The operator's return as the friction board files it. */
export interface BoardReturn {
  id: string;
  survey: string;
  runway: string;
  date: string;
  filed: boolean;
  values: Record<string, string>;
  measured: {
    n: number;
    mean: number;
    min: number;
    below: number;
    levels: { design: number; planning: number; minimum: number };
    segments: { lane: string; dir: string; from: number; to: number; min: number }[];
  };
}

/** What the form computed from the readings, handed over beside the return. */
export interface ReturnFacts {
  device?: string;
  speed?: number;
  finding?: string;
  lanes?: { A: number; B: number; C: number }[];
  /** whether the survey the return is made against is held on the platform */
  held?: boolean;
}

/* ── mappings ──────────────────────────────────────────────────────────── */

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

const offset = (): string => {
  const h = AERODROME_OFFSET_HOURS;
  return `${h >= 0 ? "+" : "-"}${String(Math.abs(h)).padStart(2, "0")}:00`;
};

/** When a survey was flown: its own time if the report gave one, else midday, at the aerodrome. */
export const flownAt = (date: string, time?: string): string =>
  `${date}T${/^\d{2}:\d{2}$/.test(time ?? "") ? time : "12:00"}:00${offset()}`;

/** A board survey as one whole survey, every lap as a run, in the frame it was flown. */
export function surveyBody(sv: BoardSurvey, runway: string): NewFrictionSurvey {
  if (sv.survey.demo) throw new Error(t("A demonstration survey is never saved on the platform."));
  const d = sv.d;
  const step = d.length > 1 ? d[1] - d[0] : d[0] || 50;
  const runs: NewFrictionSurvey["runs"] = [];
  for (const [key, laps] of Object.entries(sv.runs)) {
    const direction = key.replace(/^RW\s?/i, "");
    laps.forEach((lap, i) => {
      const segments = lap
        .map((value, j) => ({ fromM: +(d[j] - step).toFixed(2), toM: d[j], value }))
        .filter((s): s is { fromM: number; toM: number; value: number } => num(s.value) !== null && Number.isFinite(s.toM));
      if (segments.length) runs.push({ direction, lap: i + 1, segments });
    });
  }
  const lv = sv.survey.levels ?? {};
  const header: Record<string, unknown> = {};
  for (const k of ["time", "standard", "tyre", "tyre_pressure_bar", "water_film", "declared_length_m", "location", "note"] as const)
    if (sv.survey[k] !== undefined && sv.survey[k] !== null) header[k] = sv.survey[k];
  return {
    surveyId: sv.survey.id,
    kind: "survey",
    referenceKey: runwayReference(runway),
    observedAt: flownAt(sv.survey.date, sv.survey.time),
    device: sv.survey.device,
    speedKmh: num(sv.survey.speed_kmh) ?? undefined,
    levels: {
      ...(num(lv.design) !== null ? { design: lv.design } : {}),
      ...(num(lv.planning) !== null ? { planning: lv.planning } : {}),
      ...(num(lv.minimum) !== null ? { minimum: lv.minimum } : {}),
    },
    header,
    chainage: "as_flown",
    runs,
  };
}

/**
 * Where a manual test's three figures sit along the run, from the threshold
 * of the end it was taken at: the touchdown zone the board reads (900 m), and
 * the middle and last thirds of the run as the board's survey splits it.
 */
export const MANUAL_SPANS = { tdz: [0, 900], mid: [1400, 2800], roll: [2800, 4200] } as const;

/** A manual test's survey id: the day and the end, and the record's own key so two on a day stay two. */
export const manualSurveyId = (f: BoardFrictionTest, key: string): string =>
  `FR-MAN-${f.date}-${f.end}-${key.replace(/[^A-Za-z0-9]/g, "").slice(0, 6)}`;

export function manualTestBody(f: BoardFrictionTest, runway: string, key: string): NewFrictionSurvey {
  const parts = (["tdz", "mid", "roll"] as const).map((k) => ({
    fromM: MANUAL_SPANS[k][0],
    toM: MANUAL_SPANS[k][1],
    value: f[k],
  }));
  if (parts.some((p) => num(p.value) === null || p.value <= 0 || p.value > 1.5))
    throw new DataSourceError(t("Each friction figure must be a number between 0 and 1.5."), { status: 400 });
  return {
    surveyId: manualSurveyId(f, key),
    kind: "manual",
    referenceKey: runwayReference(runway),
    observedAt: flownAt(f.date),
    device: f.device || undefined,
    speedKmh: num(f.speed) ?? undefined,
    header: f.note ? { note: f.note } : {},
    chainage: "as_flown",
    runs: [{ direction: f.end, lap: 1, segments: parts }],
  };
}

/** What is still missing from a removal record. */
export function outstanding(r: BoardRemoval): string[] {
  const out: string[] = [];
  if (!r.method || r.method === "To confirm") out.push("method");
  if (!num(r.area)) out.push("area");
  if (!r.contractor || r.contractor === "To confirm" || r.contractor === "—") out.push("contractor");
  if (!num(r.hours)) out.push("hours");
  if (num(r.muAfter) === null) out.push("muAfter");
  return out;
}

export function removalAttrs(r: BoardRemoval): Record<string, unknown> {
  const contractor = r.contractor && r.contractor !== "—" ? r.contractor : null;
  return {
    end: r.end,
    method: r.method || null,
    area_m2: num(r.area),
    contractor,
    hours: num(r.hours),
    mu_before: num(r.muBefore),
    mu_after: num(r.muAfter),
    provisional: !(r.method && r.method !== "To confirm" && num(r.area)),
    outstanding: outstanding(r),
    source: "rubber planner",
  };
}

/** The removal's client id: the day and the end, as the operator's own records name it. */
export const removalClientId = (r: BoardRemoval): string => `RR-${r.date}-${r.end}`;

export function removalBody(siteId: string, r: BoardRemoval, runway: string): CompletedWorkInput {
  return {
    siteId,
    title: `Rubber removal — runway ${r.end}`,
    activity: "rubber_removal",
    completedOn: r.date,
    clientId: removalClientId(r),
    kind: "preventive",
    priority: 3,
    description: r.note || undefined,
    zoneCode: touchdownZone(runway, r.end),
    zoneFallback: runwayReference(runway),
    attrs: removalAttrs(r),
  };
}

/** The verdict the return states, in the terms its levels are written in. */
export function returnOutcome(m: BoardReturn["measured"]): "pass" | "attention" | "fail" {
  if (m.mean < m.levels.planning) return "fail";
  return m.below > 0 ? "attention" : "pass";
}

export function returnBody(r: BoardReturn, facts: ReturnFacts): NewTestInput {
  const m = r.measured;
  const v = r.values;
  const thirds = (facts.lanes ?? []).flatMap((l) => [l.A, l.B, l.C]).filter((x) => num(x) !== null);
  const summary: Record<string, string | number> = {
    survey_id: r.survey,
    return_id: r.id,
    runway: r.runway,
    readings: m.n,
    mean: +m.mean.toFixed(3),
    min: +m.min.toFixed(3),
    below_minimum: m.below,
    design_level: m.levels.design,
    maintenance_level: m.levels.planning,
    minimum_level: m.levels.minimum,
    survey_held: facts.held ? "yes" : "no",
  };
  if (thirds.length) summary.lowest_third_mu = +Math.min(...thirds).toFixed(3);
  if (facts.device) summary.device = `${facts.device}${facts.speed ? ` at ${facts.speed} km/h` : ""}`;
  for (const k of ["operator", "aerodrome", "purpose", "restriction", "notam", "verify", "prepared", "accountable", "remarks"])
    if (v[k]) summary[k] = v[k];
  return {
    testType: "friction",
    performedOn: r.date,
    title: `Friction return — RWY ${r.runway} ${r.date}`,
    performedBy: v.contractor || undefined,
    method: "ICAO CFME, continuous",
    device: summary.device as string | undefined,
    scope: `RWY ${r.runway}, ${m.n} readings${facts.lanes ? `, ${facts.lanes.length} runs` : ""}`,
    summary,
    results: m.segments.map((g) => ({
      location: `${g.lane} ${g.dir} ${g.from}–${g.to} m`,
      value: g.min,
    })),
    outcome: returnOutcome(m),
    outcomeBasis: facts.finding || t("Judged against the levels for this device and speed."),
    notes: v.actions || undefined,
    nextDue: /^\d{4}-\d{2}-\d{2}$/.test(v.nextdue ?? "") ? v.nextdue : undefined,
    source: "form",
  };
}

/** The planner's figures as the board holds them. */
export interface BoardModel {
  RUB: {
    threshold: number;
    residual: number;
    [k: string]: unknown;
  };
  RUBM: { plateau: number; muClean: number; muFloor: number; scale: number; qUncertainty: number };
}

/** One runway end's deposition rate, from the board's own traffic figures. */
export interface EndRate {
  runway: string;
  desig: string;
  rate: number;
}

/**
 * The board's figures in the platform model's units. Retained rubber after a
 * removal is a percentage on the board and grams per square metre on the
 * platform (10 % of a 300 g/m² level is 30 g/m²); each end's deposition goes
 * on its own touchdown third, because one figure for every zone was end 17's.
 */
export function modelBody(m: BoardModel, ends: EndRate[]): ModelParamsInput {
  const params: Record<string, number> = {};
  const put = (k: string, v: unknown) => {
    const x = num(v);
    if (x !== null) params[k] = x;
  };
  put("removal_level", m.RUB.threshold);
  put("residual", +((m.RUB.threshold * m.RUB.residual) / 100).toFixed(3));
  put("plateau", m.RUBM.plateau);
  put("companion_clean", m.RUBM.muClean);
  put("companion_floor", m.RUBM.muFloor);
  put("companion_scale", m.RUBM.scale);
  put("deposition_uncertainty", m.RUBM.qUncertainty);
  const zones: Record<string, Record<string, number>> = {};
  for (const e of ends) {
    const code = touchdownZone(e.runway, e.desig);
    const rate = num(e.rate);
    if (code && rate !== null) zones[code] = { deposition_per_day: +rate.toFixed(4) };
  }
  return { params, inputs: JSON.parse(JSON.stringify({ RUB: m.RUB, RUBM: m.RUBM })), zones };
}

/* ── the writes ────────────────────────────────────────────────────────── */

const runwayOf = (extra: unknown, fallback = "17/35"): string => {
  const r = (extra as { runway?: unknown } | null)?.runway;
  return typeof r === "string" && /^\d{1,2}[LCR]?\/\d{1,2}[LCR]?$/.test(r) ? r : fallback;
};

registerWrite("friction.survey", {
  permission: () => "reading:write",
  invalidate: (site) => [frictionKeys.surveys(site)],
  retryable: true,
  async send({ source, siteId, key }, rec: BoardSurvey, extra) {
    const body = surveyBody(rec, runwayOf(extra, rec.survey.runway));
    const ack = await source.importFrictionSurvey(siteId, body, key);
    return {
      id: ack.surveyId,
      reference: ack.surveyId,
      at: ack.importedAt,
      text: t("Survey {id} saved on the platform: {runs} runs, {n} readings.", {
        id: ack.surveyId,
        runs: ack.runs,
        n: ack.segments,
      }),
    };
  },
});

registerWrite("friction.test", {
  permission: () => "reading:write",
  invalidate: (site) => [frictionKeys.surveys(site)],
  retryable: true,
  async send({ source, siteId, key }, rec: BoardFrictionTest, extra) {
    const ack = await source.importFrictionSurvey(siteId, manualTestBody(rec, runwayOf(extra), key), key);
    return {
      id: ack.surveyId,
      reference: ack.surveyId,
      at: ack.importedAt,
      text: t("Friction test for RWY {end} saved on the platform.", { end: rec.end }),
    };
  },
});

registerWrite("friction.return", {
  permission: () => "test:write",
  invalidate: (site) => [frictionKeys.testRecords(site), ["testing-summary", site]],
  async send({ source, siteId }, rec: BoardReturn, extra) {
    const facts = (extra ?? {}) as ReturnFacts & { revises?: string | null };
    const body = returnBody(rec, facts);
    /* a filed return is a record: filing it again is its next revision, never a second return */
    const ack = facts.revises
      ? await source.reviseTestRecord(facts.revises, {
          ...body,
          reason: t("Corrected and filed again from the friction screen."),
        })
      : await source.createTestRecord(siteId, body);
    /* the sample holds no surveys of its own, so there is nothing to say there */
    const held = facts.held || source.kind === "mock";
    return {
      id: ack.id,
      reference: ack.reference,
      kind: "test_record",
      text: held
        ? t("{ref} filed on the platform as the operator's return.", { ref: ack.reference })
        : t("{ref} filed on the platform. The survey it is made against is not held on the platform yet.", {
            ref: ack.reference,
          }),
      tone: held ? "ok" : "warn",
    };
  },
});

registerWrite("rubber.removal", {
  permission: () => "work_order:write",
  invalidate: (site) => [frictionKeys.workOrders(site)],
  retryable: true,
  async send({ source, siteId }, rec: BoardRemoval, extra) {
    const ack = await source.recordCompletedWork(removalBody(siteId, rec, runwayOf(extra)));
    return {
      id: ack.id,
      reference: ack.number,
      at: ack.completedAt,
      kind: "work_order",
      text: ack.created
        ? t("{ref}: removal on RWY {end} saved on the platform.", { ref: ack.number, end: rec.end })
        : t("{ref}: this removal was already on the platform.", { ref: ack.number }),
    };
  },
});

registerWrite("rubber.removal.amend", {
  permission: () => "work_order:write",
  invalidate: (site) => [frictionKeys.workOrders(site)],
  retryable: true,
  async send({ source, siteId }, rec: BoardRemoval, extra) {
    const id = (extra as { platformId?: unknown } | null)?.platformId;
    /* a removal never saved before (a sample record, or one refused) is recorded now */
    if (typeof id !== "string" || !id) {
      const ack = await source.recordCompletedWork(removalBody(siteId, rec, runwayOf(extra)));
      return {
        id: ack.id,
        reference: ack.number,
        at: ack.completedAt,
        kind: "work_order",
        text: t("{ref}: removal on RWY {end} saved on the platform.", { ref: ack.number, end: rec.end }),
      };
    }
    const done = await source.amendCompletedWork(id, {
      attrs: removalAttrs(rec),
      completedOn: rec.date,
      note: rec.note || undefined,
    });
    return {
      id: done.id,
      reference: done.number,
      kind: "work_order",
      text: done.changed
        ? t("{ref}: removal record updated on the platform.", { ref: done.number })
        : t("{ref}: nothing to change on the platform.", { ref: done.number }),
    };
  },
});

registerWrite("rubber.model", {
  permission: () => "insight:run",
  invalidate: (site) => [frictionKeys.modelParams(site, RUBBER_MODEL)],
  retryable: true,
  async send({ source, siteId }, rec: BoardModel, extra) {
    const ends = ((extra as { ends?: EndRate[] } | null)?.ends ?? []).filter((e) => e && typeof e.desig === "string");
    const saved = await source.saveModelParams(siteId, RUBBER_MODEL, modelBody(rec, ends));
    const missing = saved.zonesMissing?.length ?? 0;
    return {
      id: saved.model,
      reference: t("Rubber figures"),
      at: saved.savedAt,
      text: missing
        ? t("This airport's rubber figures are saved on the platform. The runway touchdown zones are not mapped yet, so the platform's own forecast cannot use the rates for each end.")
        : t("This airport's rubber figures are saved on the platform."),
    };
  },
});
